import * as THREE from "../../libs/three.js/build/three.module.js";
import { Measure } from "../utils/Measure.js";

export class DXFExporter {

	static measurementPointSection (measurement) {
		let position = measurement.points[0].position;

		if (!position) {
			return '';
		}

		let dxfSection = `0
CIRCLE
8
layer_point
10
${position.x}
20
${position.y}
30
${position.z}
40
0.05
`;

		return dxfSection;
	}

	static measurementPolylineSection(measurement) {
		let geomCode = 8;
		if (measurement.closed) {
			geomCode += 1;
		}

		const offset = window.offsetCloud || [0, 0, 0];

		let dxfSection = `0
POLYLINE
8
layer_polyline
62
1
66
1
10
0.0
20
0.0
30
0.0
70
${geomCode}
`;

		for (let point of measurement.points) {
			const pos = point.position;
			const x = pos.x + offset[0];
			const y = pos.y + offset[1];
			const z = pos.z + offset[2];

			dxfSection += `0
VERTEX
8
0
10
${x}
20
${y}
30
${z}
70
32
`;
		}

		dxfSection += `0
SEQEND
`;

		return dxfSection;
	}


static create3DCirclePolylineDXF(p1, p2, p3, segments = 24) {
	const offset = window.offsetCloud || [0, 0, 0];

	// Convert points to vectors
	const A = new THREE.Vector3(p1.x, p1.y, p1.z);
	const B = new THREE.Vector3(p2.x, p2.y, p2.z);
	const C = new THREE.Vector3(p3.x, p3.y, p3.z);

	// Calculate circle center (intersection of perpendicular bisectors)
	const AB = B.clone().sub(A);
	const AC = C.clone().sub(A);
	const normal = AB.clone().cross(AC).normalize();

	const midAB = A.clone().add(B).multiplyScalar(0.5);
	const midAC = A.clone().add(C).multiplyScalar(0.5);

	const dirAB = AB.clone().cross(normal).normalize();
	const dirAC = AC.clone().cross(normal).normalize();

	// Plane equations: midAB + s*dirAB = midAC + t*dirAC → solve for intersection
	const matrix = new THREE.Matrix3();
	matrix.set(
		dirAB.x, -dirAC.x, normal.x,
		dirAB.y, -dirAC.y, normal.y,
		dirAB.z, -dirAC.z, normal.z
	);
	const rhs = midAC.clone().sub(midAB);

	let s;
	try {
		const inverse = new THREE.Matrix3().copy(matrix).invert();
		const result = rhs.applyMatrix3(inverse);
		s = result.x;
	} catch (e) {
		// fallback: degenerate triangle
		return '';
	}

	const center = midAB.clone().add(dirAB.clone().multiplyScalar(s));
	const radius = center.distanceTo(A);

	// Create local coordinate system (u, v) in the circle's plane
	const u = AB.clone().normalize();
	const v = normal.clone().cross(u).normalize();

	// Start building DXF
	let dxf = `0
POLYLINE
8
layer_circle
66
1
70
8
10
0.0
20
0.0
30
0.0
`;

	// Generate points around circle
	for (let i = 0; i <= segments; i++) {
		const angle = (i / segments) * 2 * Math.PI;
		const point = center.clone()
			.add(u.clone().multiplyScalar(Math.cos(angle) * radius))
			.add(v.clone().multiplyScalar(Math.sin(angle) * radius));

		// Apply global offset
		point.add(new THREE.Vector3(...offset));

		dxf += `0
VERTEX
8
layer_circle
10
${point.x}
20
${point.y}
30
${point.z}
`;
	}

	dxf += `0
SEQEND
`;

	return dxf;
}

static measurementSection(measurement) {
	const isCircle = measurement.name === 'Circle' || measurement.type === 'circle';

	if (isCircle && measurement.points.length === 3) {
		return DXFExporter.create3DCirclePolylineDXF(
			measurement.points[0].position,
			measurement.points[1].position,
			measurement.points[2].position
		);
	} else if (measurement.points.length === 1) {
		return DXFExporter.measurementPointSection(measurement);
	} else if (measurement.points.length >= 2) {
		return DXFExporter.measurementPolylineSection(measurement);
	} else {
		return '';
	}
}


	static toString(measurements) {
		if (!(measurements instanceof Array)) {
			measurements = [measurements];
		}
		measurements = measurements.filter(m => m instanceof Measure);

		let points = measurements.filter(m => (m instanceof Measure))
			.map(m => m.points)
			.reduce((a, v) => a.concat(v))
			.map(p => p.position);

		const offset = window.offsetCloud || [0, 0, 0];

		let min = new THREE.Vector3(Infinity, Infinity, Infinity);
		let max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

		for (let point of points) {
			min.min(new THREE.Vector3(
				point.x + offset[0],
				point.y + offset[1],
				point.z + offset[2]
			));
			max.max(new THREE.Vector3(
				point.x + offset[0],
				point.y + offset[1],
				point.z + offset[2]
			));
		}

		let dxfHeader = `999
DXF created from potree
0
SECTION
2
HEADER
9
$ACADVER
1
AC1006
9
$INSBASE
10
0.0
20
0.0
30
0.0
9
$EXTMIN
10
${min.x}
20
${min.y}
30
${min.z}
9
$EXTMAX
10
${max.x}
20
${max.y}
30
${max.z}
0
ENDSEC
`;

		let dxfBody = `0
SECTION
2
ENTITIES
`;

		for (let measurement of measurements) {
			dxfBody += DXFExporter.measurementSection(measurement);
		}

		dxfBody += `0
ENDSEC
`;

		let dxf = dxfHeader + dxfBody + '0\nEOF';

		return dxf;
	}
}